import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Radom, { Deposit, DepositBeneficiaryDetails, ManagedBalanceLedger, ManagedEventDepositData } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, roundAssetAmount, shortAddress, trimZeros } from '../../util/Util'
import { getMethod, getMethodLogoByTicker, ManagedPaymentMethod, depositStatusColor, depositStatusLabel } from '../../util/Managed'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import TimeText from '../../components/TimeText'
import Spinner from '../../components/Spinner'
import { Chevron } from '../../icons/Chevron'
import { Section, SectionBreaker, SectionContent, SectionItem, SubTitle } from '../../components/Shared'
import DepositChainDetails from './DepositChainDetails'
import DetailsDrawer from '../../components/DetailsDrawer'

interface DepositDetailsDrawerProps {
  visible: boolean
  depositId?: string
  onClose: () => void
}

const labelFromBeneficiaryKey = (key: string): string => key
  .split('_')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ')

const DepositDetailsDrawer = ({ visible, depositId, onClose }: DepositDetailsDrawerProps): ReactElement | null => {
  const [isLoading, setIsLoading] = useState(false)
  const [deposit, setDeposit] = useState<Deposit>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()
  const [eventData, setEventData] = useState<ManagedEventDepositData>()
  const [beneficiaryDetails, setBeneficiaryDetails] = useState<DepositBeneficiaryDetails>()

  useEffect(() => {
    if (!visible || !depositId) return

    let isMounted = true

    const fetchDepositData = async (): Promise<void> => {
      setIsLoading(true)
      setDeposit(undefined)
      setPaymentMethod(undefined)
      setManagedBalanceLedger(undefined)
      setEventData(undefined)
      setBeneficiaryDetails(undefined)

      try {
        const deposit = await Radom.getDeposit(depositId)
        if (!isMounted) return

        setDeposit(deposit)
        setPaymentMethod(getMethod(
          deposit.depositMethod.Crypto?.network || deposit.depositMethod.Fiat?.onrampNetwork || '',
          deposit.depositMethod.Crypto?.token || deposit.depositMethod.Fiat?.onrampToken
        ))

        const [ledger, event] = await Promise.all([
          Radom.listManagedBalanceLedger([deposit.id]).catch(() => []),
          Radom.getDepositEvent(deposit.id).catch(() => undefined)
        ])

        if (!isMounted) return

        if (ledger.length > 0) {
          setManagedBalanceLedger(ledger[0])
        }

        if (event?.eventData.deposit) {
          setEventData(event.eventData.deposit)
          setBeneficiaryDetails(event.eventData.deposit.beneficiaryDetails)
        }
      } catch {
        if (isMounted) {
          errorToast('Failed to load deposit')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchDepositData()

    return () => {
      isMounted = false
    }
  }, [depositId, visible])

  const isFiatDeposit = useMemo(() => !!deposit?.depositMethod.Fiat, [deposit])
  const network = deposit?.depositMethod.Crypto?.network || deposit?.depositMethod.Fiat?.onrampNetwork || ''
  const transactionHash = deposit?.depositTransaction?.transactionHash || eventData?.transactionHash
  const senderAddresses = deposit?.depositTransaction?.senderAddresses || eventData?.senderAddresses || []
  const transactionAmount = deposit?.depositTransaction?.amount ?? deposit?.grossAmount

  if (!visible) {
    return null
  }

  return <DetailsDrawer
    visible={visible}
    onClose={onClose}
    eyebrow='Deposit'
    title={deposit?.depositAccountName || 'Deposit details'}
    width={590}
    ariaLabel='Deposit details'>
    {
      isLoading &&
            <div style={{ display: 'flex', justifyContent: 'center', padding: '72px 0' }}>
              <Spinner />
            </div>
    }

    {
      !isLoading && deposit && paymentMethod &&
            <>
              <Section>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: 0,
                  flexWrap: 'wrap'
                }}>
                  {isFiatDeposit && <>
                    <img src={getMethod('Fiat', deposit.depositMethod.Fiat?.fiatCurrency).logo as string} style={{ width: 26, height: 'auto', maxHeight: 26 }} />
                    <span style={{ fontSize: 28, fontWeight: 500 }}>
                      {trimZeros((deposit.fiatGrossAmount ?? 0).toString())}
                    </span>
                    <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                      {getMethod('Fiat', deposit.depositMethod.Fiat?.fiatCurrency).ticker}
                    </span>
                    <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 14, height: 14, transform: 'rotate(-90deg)' }} />
                  </>}
                  <img src={paymentMethod.logo} style={{ width: 26, height: 'auto', maxHeight: 26 }} />
                  <span style={{ fontSize: 28, fontWeight: 500 }}>
                    {trimZeros((isFiatDeposit ? deposit.netAmount : deposit.grossAmount).toString())}
                  </span>
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>{paymentMethod.ticker}</span>
                </div>

                <SectionContent>
                  <SectionItem>
                    <SubTitle>ID</SubTitle>
                    <span style={{ wordBreak: 'break-all', fontSize: 14 }}>{shortAddress(deposit.id, 6)}</span>
                  </SectionItem>
                  <SectionItem>
                    <SubTitle>Type</SubTitle>
                    <span style={{ fontSize: 14 }}>{deposit.depositMethod.Crypto ? 'Crypto deposit' : 'Fiat deposit'}</span>
                  </SectionItem>
                  <SectionItem>
                    <SubTitle>Deposit account</SubTitle>
                    <span style={{ wordBreak: 'break-word', fontSize: 14 }}>{deposit.depositAccountName || deposit.depositAccountId}</span>
                  </SectionItem>
                  <SectionItem>
                    <SubTitle>Status</SubTitle>
                    <span style={{ fontSize: 14, color: depositStatusColor(deposit.status) }}>
                      {depositStatusLabel(deposit.status)}
                    </span>
                  </SectionItem>
                  <SectionItem>
                    <SubTitle>Deposited at</SubTitle>
                    <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
                  </SectionItem>
                </SectionContent>
              </Section>

              {managedBalanceLedger &&
                <Section>
                  <SubTitle style={{ fontSize: 18 }}>Ledger</SubTitle>
                  <SectionContent>
                    <SectionItem>
                      <SubTitle>Balance before</SubTitle>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>
                          {roundAssetAmount(paymentMethod.ticker, Number(managedBalanceLedger.balanceBefore))}
                        </span>
                        <span style={{ color: RADOM_COLORS.GRAY4, fontSize: 14 }}>
                          {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                        </span>
                      </div>
                    </SectionItem>
                    <SectionItem>
                      <SubTitle>Balance after</SubTitle>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>
                          {roundAssetAmount(paymentMethod.ticker, Number(managedBalanceLedger.balanceAfter))}</span>
                        <span style={{ color: RADOM_COLORS.GRAY4, fontSize: 14 }}>
                          {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                        </span>
                      </div>
                    </SectionItem>
                  </SectionContent>
                </Section>}

              <Section>
                <SubTitle style={{ fontSize: 18 }}>Deposit details</SubTitle>
                <SectionContent>
                  <SectionItem>
                    <SubTitle>Asset</SubTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PaymentMethodDisplay
                        style={{ marginRight: 4 }}
                        paymentMethod={paymentMethod}
                        method={{ network: deposit.depositMethod.Crypto?.network || 'Fiat' }}
                        includeNetworkName
                        disableTooltip={false} />
                    </div>
                  </SectionItem>

                  {deposit.depositMethod.Fiat && deposit.depositMethod.Fiat.onrampNetwork !== 'Fiat' &&
                    <SectionItem>
                      <SubTitle>Fiat gross deposit</SubTitle>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <PaymentMethodDisplay
                          method={{ network: 'Fiat', token: deposit.depositMethod.Fiat.fiatCurrency }}
                          excludeName />
                        <span style={{ fontSize: 14 }}>
                          {trimZeros((deposit.fiatGrossAmount ?? 0).toString())}
                        </span>
                        <span style={{ fontSize: 14 }}>
                          {getMethod('Fiat', deposit.depositMethod.Fiat.fiatCurrency).ticker}
                        </span>
                      </div>
                    </SectionItem>}

                  <SectionItem>
                    <SubTitle>Gross deposit</SubTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PaymentMethodDisplay
                        style={{ marginRight: 4 }}
                        method={{
                          network: deposit.depositMethod.Crypto?.network || deposit.depositMethod.Fiat?.onrampNetwork || '',
                          token: deposit.depositMethod.Crypto?.token || deposit.depositMethod.Fiat?.onrampToken
                        }}
                        excludeName
                        disableTooltip={false} />
                      <span style={{ fontSize: 14 }}>{trimZeros(deposit.grossAmount.toString())}</span>
                      <span style={{ fontSize: 14 }}>{paymentMethod.ticker}</span>
                    </div>
                  </SectionItem>

                  <SectionItem>
                    <SubTitle>Net deposit</SubTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PaymentMethodDisplay
                        style={{ marginRight: 4 }}
                        method={{
                          network: deposit.depositMethod.Crypto?.network || deposit.depositMethod.Fiat?.onrampNetwork || '',
                          token: deposit.depositMethod.Crypto?.token || deposit.depositMethod.Fiat?.onrampToken
                        }}
                        excludeName
                        disableTooltip={false} />
                      <span style={{ fontSize: 14 }}>{trimZeros(deposit.netAmount.toString())}</span>
                      <span style={{ fontSize: 14 }}>{paymentMethod.ticker}</span>
                    </div>
                  </SectionItem>

                  <SectionItem>
                    <SubTitle>Fee</SubTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PaymentMethodDisplay
                        style={{ marginRight: 4 }}
                        method={{
                          network: deposit.depositMethod.Crypto?.network || deposit.depositMethod.Fiat?.onrampNetwork || '',
                          token: deposit.depositMethod.Crypto?.token || deposit.depositMethod.Fiat?.onrampToken
                        }}
                        excludeName
                        disableTooltip={false} />
                      <span style={{ fontSize: 14 }}>{trimZeros(deposit.fee.toString())}</span>
                      <span style={{ fontSize: 14 }}>{paymentMethod.ticker}</span>
                    </div>
                  </SectionItem>
                </SectionContent>

                {deposit.conversionRates.length > 0 && <>
                  <SectionBreaker />
                  <div>
                    <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Conversion rates</SubTitle>
                    {deposit.conversionRates.map((d, i) =>
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <span>1</span>
                          {getMethodLogoByTicker(d.from) &&
                            <img src={getMethodLogoByTicker(d.from)} style={{ width: 15, height: 'auto' }} />}
                          {d.from}
                        </div>
                        <div>=</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <span>{Number(d.rate)}</span>
                          {getMethodLogoByTicker(d.to) &&
                            <img src={getMethodLogoByTicker(d.to)} style={{ width: 15, height: 'auto' }} />}
                          {d.to}
                        </div>
                      </div>
                    )}
                  </div>
                </>}
              </Section>

              {deposit.depositMethod.Crypto &&
                <DepositChainDetails
                  network={network}
                  transactionHash={transactionHash}
                  senderAddresses={senderAddresses}
                  amount={transactionAmount}
                  ticker={paymentMethod.ticker}
                  showEmpty
                />}

              {isFiatDeposit && beneficiaryDetails &&
                <Section>
                  <SubTitle style={{ fontSize: 18 }}>Sender details</SubTitle>
                  <SectionContent>
                    {Object.entries(beneficiaryDetails)
                      .filter(([key, val]) => val != null && val !== '' && key !== 'recipient_name')
                      .map(([key, val]) => (
                        <SectionItem key={key}>
                          <SubTitle>{labelFromBeneficiaryKey(key)}</SubTitle>
                          <span>{val}</span>
                        </SectionItem>
                      ))}
                  </SectionContent>
                </Section>}
            </>
    }
  </DetailsDrawer>
}

export default DepositDetailsDrawer
